import { updateLesson, updateLessonTool } from '@/data/services/lessonServices';
import { isPulished } from '@/functions/handleCourseStatusIcon';
import CoursePath from '@/models/CoursePath';
import Lesson from '@/models/Lesson';
import { TempLessonBlock } from '@/models/_deprecated/LessonBlock';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { getLessonChanges } from '@/utils/lesson/box';
import { useMutation } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useState } from 'react';
import useLessonBox from './LessonBox.hooks';
import LessonBoxForm, { LessonFields } from './LessonBoxForm';

type LessonBoxProps = {
  lesson: Lesson;
  block?: TempLessonBlock;
  coursePath: CoursePath;
  onEdit: (isEditing: boolean) => void;
};

export function LessonBoxService({
  lesson,
  block,
  coursePath,
  onEdit,
}: LessonBoxProps) {
  const { lessonsInBlockCacheManager, invalidateBlock, updateLessons } =
    useLessonBox();
  const [hasChanges, setHasChanges] = useState(false);

  const handleUpdate = async (lessonUpdate: Partial<LessonFields>) => {
    let auxLesson: Lesson = lesson;
    const lessonsChanges = await getLessonChanges(lesson, lessonUpdate);

    if (isPulished(lesson.status)) {
      auxLesson = await updateLesson({
        slugCourseName: coursePath.slug,
        blockId: block?.id,
        lessonId: lesson.id,
      });
    }

    const updatedLesson = await updateLesson(
      {
        slugCourseName: coursePath.slug,
        blockId: block?.id,
        lessonId: auxLesson.id,
      },
      lessonsChanges,
    );

    return updatedLesson;
  };

  const updateMainTool = async (payload: { tool?: number; id?: number }) => {
    if (!payload.tool || !payload.id) return;
    const updatedLesson = await updateLessonTool({
      lessonId: payload.id,
      toolId: payload.tool,
    });
    return updatedLesson;
  };

  type PosUpdateArgs = {
    input: Partial<LessonFields>;
    output: Lesson;
  };

  const handlePosUpdateLesson = async ({ input, output }: PosUpdateArgs) => {
    const lessonsChanges = await getLessonChanges(lesson, input);
    await updateMainTool({
      id: output.id,
      tool: lessonsChanges.tools?.[0],
    });
  };

  const { mutate: posUpdateLesson } = useMutation(handlePosUpdateLesson, {
    onError: error => {
      alert.error(getErrorMessage(error));
    },
  });

  const { mutate: onUpdateLesson, isPending } = useMutation(handleUpdate, {
    onMutate: () => {
      onEdit(true);
    },
    onSuccess: async (data, variables) => {
      posUpdateLesson({ input: variables, output: data });
      lessonsInBlockCacheManager.update(
        { ...data, id: lesson.id },
        {
          newId: data.id,
        },
      );
      setHasChanges(true);
      // TODO: remove this in the future - update lessonBlockWithLessons cache
      block && invalidateBlock(block.id);
    },
    onSettled: () => {
      onEdit(false);
    },
    onError: error => {
      alert.error(getErrorMessage(error));
    },
  });

  const onSubmit = (params: {
    lesson: Partial<Lesson>;
    validated: boolean;
  }) => {
    if (!params.validated) return;
    setHasChanges(false);
    onUpdateLesson(params.lesson);
  };

  return (
    <LessonBoxForm
      lesson={lesson}
      block={block}
      course={coursePath}
      onSubmit={debounce(onSubmit, 500)}
      isLoading={isPending}
      hasChanges={hasChanges}
      updateLessons={updateLessons}
    />
  );
}
